import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ResourceManager } from './components/ResourceManager';
import ImageUploadGallery from '@/components/ImageUploadGallery';
import { supabase } from '@/lib/supabaseClient';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Plus, Download, Upload, Eye, Image as ImageIcon, Edit, Trash2 } from 'lucide-react';
import { useData } from '@/contexts/DataContext';
import ImageGalleryModal from '@/components/ImageGalleryModal';

const CURATED_GALLERY_IMAGES = [
  
];

const GALLERY_EDITOR_DEFAULTS = {
  title: '',
  description: '',
  images: [],
  category_id: '',
  is_featured: false,
  display_order: 0,
};

const GalleryModule = () => {
  const { properties, blogPosts, heroSlides, galleryImages, galleryCategories, loading } = useData();
  const [categories, setCategories] = useState([]);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filteredRecords, setFilteredRecords] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [allRecords, setAllRecords] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [editingGalleryRecord, setEditingGalleryRecord] = useState(null);
  const [galleryEditorForm, setGalleryEditorForm] = useState({ ...GALLERY_EDITOR_DEFAULTS });
  const [galleryEditorSaving, setGalleryEditorSaving] = useState(false);
  const [deletingGalleryId, setDeletingGalleryId] = useState(null);
  const fileInputRef = useRef();

  useEffect(() => {
    fetchCategories();
    fetchAllRecords();
  }, []);

  const fetchAllRecords = async () => {
    const { data, error } = await supabase
      .from('gallery_images')
      .select('*')
      .order('display_order');
    if (!error) {
      setAllRecords(data || []);
    }
  };

  const fetchCategories = async () => {
    const { data, error } = await supabase
      .from('gallery_categories')
      .select('*')
      .eq('is_active', true)
      .order('display_order');
    if (!error) {
      const existing = data || [];
      if (!existing || existing.length === 0) {
        // Seed default categories if none exist
        const defaults = [
          { name: 'Plot', description: 'Land plots and parcels', display_order: 0, is_active: true },
          { name: 'Houses', description: 'Completed houses and residences', display_order: 1, is_active: true },
          { name: 'House Plans', description: 'Architectural house plans and layouts', display_order: 2, is_active: true },
        ];
        const { data: inserted, error: insertErr } = await supabase.from('gallery_categories').insert(defaults).select();
        if (!insertErr) {
          setCategories(inserted || defaults);
          return;
        }
      }

      setCategories(existing);
    } else {
      // If the gallery_categories table doesn't exist in Supabase, fall back to local defaults
      console.error('Error fetching categories:', error);
      const msg = (error && error.message) || String(error);
      if (msg.includes("Could not find the table") || msg.includes('does not exist')) {
        const localDefaults = [
          { id: `local-plot`, name: 'Plot', description: 'Land plots and parcels', display_order: 0, is_active: true },
          { id: `local-houses`, name: 'Houses', description: 'Completed houses and residences', display_order: 1, is_active: true },
          { id: `local-houseplans`, name: 'House Plans', description: 'Architectural house plans and layouts', display_order: 2, is_active: true },
        ];
        setCategories(localDefaults);
        return;
      }
      // otherwise just log the error and leave categories empty
    }
  };

  const fetchFilteredRecords = async (categoryIdValue = selectedCategory) => {
    if (!categoryIdValue || categoryIdValue === 'all' || (typeof categoryIdValue === 'string' && categoryIdValue.startsWith('local-'))) {
      setFilteredRecords(null);
      return;
    }

    const { data, error } = await supabase
      .from('gallery_images')
      .select('*')
      .eq('category_id', categoryIdValue)
      .order('display_order');

    if (!error) {
      setFilteredRecords(data || []);
    }
  };

  useEffect(() => {
    fetchFilteredRecords();
  }, [selectedCategory]);

  const handleCreateCategory = async () => {
    const name = newCategory.name ? newCategory.name.trim() : '';
    const description = newCategory.description ? newCategory.description.trim() : '';
    if (!name) {
      alert('Please enter a category name');
      return;
    }

    try {
      // compute safe display order (max existing + 1)
      const maxOrder = (categories || []).reduce((max, c) => Math.max(max, Number(c.display_order) || 0), -1);
      const display_order = maxOrder + 1;

      const { data, error } = await supabase
        .from('gallery_categories')
        .insert([{ name, description, display_order, is_active: true }])
        .select();

        if (error) {
          console.error('Create category error:', error);
          const msg = (error && error.message) || String(error);
          if (msg.includes("Could not find the table") || msg.includes('does not exist')) {
            // Create a local fallback category so the dashboard remains usable
            const localCategory = { id: `local-${Date.now()}`, name, description, display_order, is_active: true };
            setCategories(prev => [...(prev || []), localCategory]);
            setNewCategory({ name: '', description: '' });
            setCategoryDialogOpen(false);
            setSelectedCategory(localCategory.id);
            alert('Created local category (database table missing). Please create the table in Supabase to persist changes.');
            return;
          }
          alert('Could not create category: ' + (error.message || JSON.stringify(error)));
          return;
        }

        const inserted = Array.isArray(data) ? data[0] : data;
        // refresh categories and select the new one
        await fetchCategories();
        setNewCategory({ name: '', description: '' });
        setCategoryDialogOpen(false);
        if (inserted && inserted.id) setSelectedCategory(inserted.id);
    } catch (err) {
      console.error('Unexpected error creating category:', err);
      alert('Unexpected error: ' + (err.message || String(err)));
    }
  };


  const getFormFields = () => [
    { name: 'title', label: 'Title', placeholder: 'Beautiful Home Interior' },
    { name: 'images', label: 'Gallery Images', type: 'custom', component: ImageUploadGallery },
    { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Optional description' },
    { name: 'category_id', label: 'Category', type: 'select', options: categories.map(cat => ({
      label: cat.name,
      value: cat.id
    }))},
    { name: 'is_featured', label: 'Mark as Featured', type: 'checkbox' },
    { name: 'display_order', label: 'Display Order', type: 'number', placeholder: '0' },
  ];

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Uncategorized';
  };

  const galleryRecords = useMemo(() => {
    if (allRecords && allRecords.length > 0) {
      return allRecords;
    }
    return galleryImages || [];
  }, [allRecords, galleryImages]);

  const openGalleryEditor = (recordId) => {
    const record = galleryRecords.find((item) => item.id === recordId);
    if (!record) {
      alert('Unable to find this gallery entry.');
      return;
    }
    const normalizedImages = Array.isArray(record.images) && record.images.length > 0
      ? record.images
      : (record.image_url ? [record.image_url] : []);
    setEditingGalleryRecord(record);
    setGalleryEditorForm({
      title: record.title || '',
      description: record.description || '',
      images: normalizedImages,
      category_id: record.category_id || '',
      is_featured: Boolean(record.is_featured),
      display_order: Number(record.display_order) || 0,
    });
  };

  const closeGalleryEditor = () => {
    setEditingGalleryRecord(null);
    setGalleryEditorForm({ ...GALLERY_EDITOR_DEFAULTS });
  };

  const handleGalleryEditorChange = (name, value) => {
    setGalleryEditorForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleGalleryUpdate = async (event) => {
    event.preventDefault();
    if (!editingGalleryRecord) return;

    const images = Array.isArray(galleryEditorForm.images)
      ? galleryEditorForm.images.filter((img) => Boolean(img))
      : [];

    setGalleryEditorSaving(true);
    try {
      const payload = {
        title: galleryEditorForm.title,
        description: galleryEditorForm.description,
        category_id: galleryEditorForm.category_id || null,
        is_featured: Boolean(galleryEditorForm.is_featured),
        display_order: Number(galleryEditorForm.display_order) || 0,
        images,
        image_url: images[0] || null,
      };

      const { error } = await supabase
        .from('gallery_images')
        .update(payload)
        .eq('id', editingGalleryRecord.id);

      if (error) {
        throw error;
      }

      await fetchAllRecords();
      await fetchFilteredRecords();
      alert('Gallery entry updated successfully.');
      closeGalleryEditor();
    } catch (err) {
      console.error('Error updating gallery record:', err);
      alert('Could not update gallery entry: ' + (err.message || String(err)));
    } finally {
      setGalleryEditorSaving(false);
    }
  };

  const handleGalleryDelete = async (recordId) => {
    if (!recordId) return;
    const confirmed = window.confirm('Delete this gallery entry and all of its images?');
    if (!confirmed) return;

    setDeletingGalleryId(recordId);
    try {
      const { error } = await supabase
        .from('gallery_images')
        .delete()
        .eq('id', recordId);
      if (error) throw error;

      await fetchAllRecords();
      await fetchFilteredRecords();
      alert('Gallery entry deleted.');
      if (editingGalleryRecord && editingGalleryRecord.id === recordId) {
        closeGalleryEditor();
      }
    } catch (err) {
      console.error('Error deleting gallery record:', err);
      alert('Could not delete gallery entry: ' + (err.message || String(err)));
    } finally {
      setDeletingGalleryId(null);
    }
  };

  const exportToCSV = () => {
    const headers = ['title', 'images', 'description', 'category_id', 'is_featured', 'display_order'];
    const csvContent = [
      headers.join(','),
      ...allRecords.map(record => headers.map(h => {
        if (h === 'images') {
          return Array.isArray(record.images) ? record.images.join(';') : record.images || '';
        }
        return `"${record[h] || ''}"`;
      }).join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'gallery_images.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        alert('CSV must have headers and at least one row');
        return;
      }
      const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
      const requiredHeaders = ['title'];
      const missing = requiredHeaders.filter(h => !headers.includes(h));
      if (missing.length) {
        alert(`Missing required headers: ${missing.join(', ')}`);
        return;
      }
      const galleries = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.replace(/"/g, '').trim());
        const obj = {};
        headers.forEach((h, i) => {
          if (h === 'images') {
            obj[h] = values[i] ? values[i].split(';').filter(img => img.trim()) : [];
          } else {
            obj[h] = values[i] || '';
          }
        });
        // transform
        obj.is_featured = obj.is_featured === 'true';
        obj.display_order = Number(obj.display_order) || 0;
        obj.category_id = obj.category_id ? obj.category_id : null;
        return obj;
      });
      const { error } = await supabase.from('gallery_images').insert(galleries);
      if (error) throw error;
      alert('Bulk upload successful');
      await fetchAllRecords();
      await fetchFilteredRecords();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  // Build website gallery items (same logic as GalleryPage)
  const websiteGalleryItems = useMemo(() => {
    const items = [];

    // Helper to avoid duplicates
    const pushImage = (url, meta) => {
      if (!url) return;
      items.push({ url, ...meta });
    };

    // Property images
    (properties || []).forEach((p) => {
      const seen = new Set();
      if (p.featured_image) {
        seen.add(p.featured_image);
        pushImage(p.featured_image, {
          type: 'property',
          title: p.title,
          subtitle: p.location,
          id: p.id,
          category: p.category,
          location: p.location,
          link: `/property/${p.id}`,
        });
      }
      (p.images || []).forEach((img) => {
        if (img && !seen.has(img)) {
          seen.add(img);
          pushImage(img, {
            type: 'property',
            title: p.title,
            subtitle: p.location,
            id: p.id,
            category: p.category,
            location: p.location,
            link: `/property/${p.id}`,
          });
        }
      });
    });

    // Hero slides
    (heroSlides || []).forEach((s) => {
      if (s.background_image) {
        pushImage(s.background_image, {
          type: 'hero',
          title: s.title,
          subtitle: s.subtitle,
        });
      }
      if (s.interior_image) {
        pushImage(s.interior_image, {
          type: 'hero',
          title: s.title,
          subtitle: s.subtitle,
        });
      }
    });

    // Blog featured images
    (blogPosts || []).forEach((b) => {
      if (b.featured_image) {
        pushImage(b.featured_image, {
          type: 'blog',
          title: b.title,
          subtitle: b.excerpt,
          link: `/blog/${b.id}`,
        });
      }
    });

    // Managed gallery images from database
    (galleryRecords || []).forEach((img) => {
      const recordImages = Array.isArray(img.images) && img.images.length > 0
        ? img.images
        : (img.image_url ? [img.image_url] : []);
      const resolvedCategory = img.category_name || getCategoryName(img.category_id) || 'Gallery';
      recordImages.forEach((imageUrl, imageIndex) => {
        pushImage(imageUrl, {
          type: 'gallery',
          title: img.title,
          subtitle: img.description || resolvedCategory,
          category: resolvedCategory,
          recordId: img.id,
          record: img,
          imageIndex,
        });
      });
    });

    // Fallback to curated images if no managed gallery images exist
    if (!galleryRecords || galleryRecords.length === 0) {
      CURATED_GALLERY_IMAGES.forEach((url, index) => {
        pushImage(url, {
          type: 'gallery',
          title: 'Nyinimu Home',
          subtitle: `Showcase ${index + 1}`,
        });
      });
    }

    return items;
  }, [properties, blogPosts, heroSlides, galleryRecords, categories]);

  const handleImageClick = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleBulkUpload}
        accept=".csv"
        style={{ display: 'none' }}
      />
      <div className="space-y-6">
        {/* Category Management */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-semibold">Gallery Management</h2>
            <p className="text-gray-600">Manage gallery images and categories</p>
          </div>
          <div className="flex items-center gap-3">
            <label className="text-sm text-stone-600">Filter:</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="rounded-md border border-stone-300 bg-white px-3 py-2 text-sm"
            >
              <option value="all">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
          <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Add Category
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Category</DialogTitle>
                <DialogDescription>
                  Add a new category for organizing gallery images.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="category-name">Category Name</Label>
                  <Input
                    id="category-name"
                    value={newCategory.name}
                    onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Interior Design"
                  />
                </div>
                <div>
                  <Label htmlFor="category-description">Description (Optional)</Label>
                  <Textarea
                    id="category-description"
                    value={newCategory.description}
                    onChange={(e) => setNewCategory(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of this category"
                  />
                </div>
                <Button onClick={handleCreateCategory} className="w-full">
                  Create Category
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Gallery Images Management */}
        <ResourceManager
          table="gallery_images"
          title="Gallery Images"
          description="Upload and manage images for the public gallery."
          externalRecords={filteredRecords}
          columns={[
            { label: 'Image', accessor: 'images', render: (value) => {
              const images = Array.isArray(value) ? value : [];
              const featuredImage = images.length > 0 ? images[0] : null;
              return featuredImage ? (
                <img src={featuredImage} alt="Gallery" className="w-16 h-16 object-cover rounded" />
              ) : 'No image';
            }},
            { label: 'Title', accessor: 'title' },
            { label: 'Category', accessor: 'category_id', render: (value) => getCategoryName(value) },
            { label: 'Featured', accessor: 'is_featured', render: (value) => value ? 'Yes' : 'No' },
            { label: 'Order', accessor: 'display_order' },
            { label: 'Created', accessor: 'created_at', render: (value) => format(new Date(value), 'MMM dd, yyyy') },
          ]}
          formFields={getFormFields}
          defaultValues={{
            title: '',
            images: [],
            description: '',
            category_id: '',
            is_featured: false,
            display_order: 0,
          }}
          transformBeforeSave={(payload) => {
            const images = Array.isArray(payload.images) ? payload.images : [];
            const featuredImage = images.length > 0 ? images[0] : '';

            let categoryId = payload.category_id;
            if (!categoryId || (typeof categoryId === 'string' && categoryId.startsWith('local-'))) {
              categoryId = null;
            }

            return {
              ...payload,
              image_url: featuredImage, // Keep for backward compatibility
              images: images,
              category_id: categoryId,
              display_order: Number(payload.display_order) || 0,
            };
          }}
          handleEdit={(record) => {
            // Ensure images is an array for existing records
            const images = record.images
              ? (Array.isArray(record.images) ? record.images : [record.images])
              : (record.image_url ? [record.image_url] : []);

            return {
              ...record,
              images,
            };
          }}
          extraButtons={[
            { label: 'Export CSV', onClick: exportToCSV, icon: Download },
            { label: uploading ? 'Uploading...' : 'Bulk Upload', onClick: () => fileInputRef.current.click(), disabled: uploading, icon: Upload },
          ]}
        />

        {/* Website Gallery Overview */}
        <div className="mt-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-semibold flex items-center gap-2">
                <Eye className="w-6 h-6 text-[#81be00]" />
                Website Gallery Overview
              </h2>
              <p className="text-gray-600">All images displayed on the public gallery page</p>
            </div>
            <Badge variant="outline" className="text-sm">
              {websiteGalleryItems.length} images
            </Badge>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#81be00] mx-auto mb-4"></div>
              <p className="text-gray-600">Loading gallery images...</p>
            </div>
          ) : websiteGalleryItems.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No images found in the website gallery
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {websiteGalleryItems.map((item, idx) => (
                <div key={idx} className="relative group cursor-pointer" onClick={() => handleImageClick(idx)}>
                  <div className="aspect-square overflow-hidden rounded-lg bg-gray-100">
                    <img
                      src={item.url}
                      alt={item.title || `Image ${idx + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    />
                  </div>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-lg"></div>
                  <div className="absolute top-1 left-1">
                    <Badge className="text-xs bg-white/90 text-stone-800 capitalize">
                      {item.type}
                    </Badge>
                  </div>
                  {item.type === 'gallery' && item.recordId && (
                    <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openGalleryEditor(item.recordId);
                        }}
                        className="rounded bg-white/90 p-1 text-stone-700 shadow hover:bg-white"
                        aria-label="Edit gallery entry"
                        title="Edit gallery entry"
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGalleryDelete(item.recordId);
                        }}
                        className="rounded bg-white/90 p-1 text-red-600 shadow hover:bg-white disabled:opacity-60"
                        aria-label="Delete gallery entry"
                        title="Delete gallery entry"
                        disabled={deletingGalleryId === item.recordId}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                  {item.subtitle && (
                    <div className="absolute bottom-1 left-1 right-1 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="bg-black/50 rounded px-2 py-1 truncate">
                        {item.subtitle}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit Gallery Entry */}
      <Dialog open={Boolean(editingGalleryRecord)} onOpenChange={(open) => { if (!open) closeGalleryEditor(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Gallery Entry</DialogTitle>
            <DialogDescription>
              Update the copy, category, order, or images for this gallery item. Changes reflect on the public gallery immediately.
            </DialogDescription>
          </DialogHeader>
          {editingGalleryRecord && (
            <form className="space-y-5" onSubmit={handleGalleryUpdate}>
              <div>
                <Label htmlFor="gallery-editor-title">Title</Label>
                <Input
                  id="gallery-editor-title"
                  value={galleryEditorForm.title}
                  onChange={(e) => handleGalleryEditorChange('title', e.target.value)}
                  placeholder="Beautiful Home Interior"
                />
              </div>
              <div>
                <Label htmlFor="gallery-editor-description">Description</Label>
                <Textarea
                  id="gallery-editor-description"
                  value={galleryEditorForm.description}
                  onChange={(e) => handleGalleryEditorChange('description', e.target.value)}
                  placeholder="Optional description displayed under the image."
                  rows={3}
                />
              </div>
              <div>
                <Label>Gallery Images</Label>
                <ImageUploadGallery
                  value={galleryEditorForm.images}
                  onChange={(value) => handleGalleryEditorChange('images', value)}
                />
                <p className="text-xs text-stone-500 mt-1">The first image is used as the thumbnail in listings.</p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="gallery-editor-category">Category</Label>
                  <select
                    id="gallery-editor-category"
                    value={galleryEditorForm.category_id ?? ''}
                    onChange={(e) => handleGalleryEditorChange('category_id', e.target.value)}
                    className="mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 focus:border-lime-500 focus:ring-2 focus:ring-lime-200"
                  >
                    <option value="">Select category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="gallery-editor-order">Display Order</Label>
                  <Input
                    id="gallery-editor-order"
                    type="number"
                    value={galleryEditorForm.display_order}
                    onChange={(e) => handleGalleryEditorChange('display_order', e.target.value)}
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-stone-700">
                <input
                  type="checkbox"
                  checked={Boolean(galleryEditorForm.is_featured)}
                  onChange={(e) => handleGalleryEditorChange('is_featured', e.target.checked)}
                  className="h-4 w-4 rounded border-stone-300 text-lime-600 focus:ring-lime-500"
                />
                Mark as featured
              </label>
              <div className="flex flex-col gap-3 pt-2 sm:flex-row">
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={galleryEditorSaving}
                >
                  {galleryEditorSaving ? 'Saving Changes...' : 'Save Changes'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={closeGalleryEditor}
                  disabled={galleryEditorSaving}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      <ImageGalleryModal
        images={websiteGalleryItems.map(item => item.url)}
        initialIndex={lightboxIndex}
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        currentMetaList={websiteGalleryItems}
      />
    </>
  );
};

export default GalleryModule;
